import React, { useState, useRef, useEffect } from 'react';
import { Send, Square, Trash2, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AssistantMessage } from '@/components/assistant';
import { cn } from '@/lib/utils';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: any[];
  isStreaming?: boolean;
}

interface PlaygroundChatProps {
  messages: Message[];
  isLoading: boolean;
  onSendMessage: (message: string) => void;
  onStopGeneration: () => void;
  onClearMessages: () => void;
  disabled?: boolean;
  className?: string;
}

export function PlaygroundChat({
  messages,
  isLoading,
  onSendMessage,
  onStopGeneration,
  onClearMessages,
  disabled = false,
  className,
}: PlaygroundChatProps) {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [input]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!input.trim() || isLoading || disabled) return;

    const message = input;
    setInput('');
    onSendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Chat with Agent</span>
        </div>
        {messages.length > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onClearMessages}
            title="Clear conversation"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <MessageCircle className="h-8 w-8 mx-auto mb-3 text-primary/50" />
              <p className="text-sm font-medium mb-2">Interactive Mode</p>
              <p className="text-xs max-w-[250px] mx-auto">
                Chat with your agent in real-time. Messages are sent to the running agent
                and responses are streamed back.
              </p>
            </div>
          ) : (
            messages.map((message) => (
              <AssistantMessage
                key={message.id}
                message={message}
              />
            ))
          )}
          {isLoading && messages[messages.length - 1]?.role === 'assistant' && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={onStopGeneration}
                className="text-xs"
              >
                <Square className="h-3 w-3 mr-1.5 fill-current" />
                Stop generating
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="border-t p-3">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Send a message to the agent..."
            className="min-h-[40px] max-h-[150px] resize-none text-sm"
            rows={1}
            disabled={isLoading || disabled}
          />
          <Button
            type="submit"
            size="icon"
            disabled={!input.trim() || isLoading || disabled}
            className="h-10 w-10 shrink-0"
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}

export default PlaygroundChat;
